import {_, React, observer, observable, computed, IDisposer, action, useLocalStore, useEffect, useLayoutEffect, classNames, useAsObservableSource, Fragment} from '@api'
import {useVipLocalStore, VipLocalStore} from '@modules'
import {CloudPDFEntry, ICloudPDFEntry, IQueuedPdfServerJob} from '@modules/pdf'
import {StoresInstance} from '@stores'
import {CollapsibleStateful} from 'centric-ui-components'
import * as css from './PdfOptionsPanel.css'
import {IFlexLayoutTabProps} from '@stores'
import {PdfSidebarContent} from './'

type MyProps = { className?: string } & Partial<IFlexLayoutTabProps>

/// region Store

export class PdfOptionsPanelStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: Omit<MyProps, 'className'>) {
    super(stores)
  }

  registerEffects = () => {
    const _toDispose: Array<IDisposer> = []

    return () => {
      _toDispose.forEach(d => d())
    }
  }

  @observable selectedEntry : ICloudPDFEntry
}

export const PdfOptionsPanelStoreContext = React.createContext<PdfOptionsPanelStore>(null)
export const usePdfOptionsPanelStore = () => React.useContext(PdfOptionsPanelStoreContext)

/// endregion

/// region Components

export const PdfOptionsPanel = observer<MyProps>(function PdfOptionsPanel({className, ...props}) {
  const obsProps = useAsObservableSource({flexNode: props.flexNode}),
    store = useVipLocalStore((stores) => new PdfOptionsPanelStore(stores, obsProps)),
    {catalog: {isServerPDFAvailable}} = store

  useEffect(() => store.registerEffects(), [])

  return (
    <PdfOptionsPanelStoreContext.Provider value={store}>
      <div className={classNames(css.root, className)}>
        {DEBUG && isServerPDFAvailable && <ServerPDFToolPanel />}

        <PdfSidebarContent/>
      </div>
    </PdfOptionsPanelStoreContext.Provider>)
})

/// endregion

export const ServerPDFToolPanel = observer(function ServerPDFToolPanel() {
    return (<div className={css.serverPanel}>
      <PDFOptionsPanelHeader />
      <PDFOptionsPanelJobsSection/>
      <PDFOptionsCurrentJobSection/>
    </div>)
})

export const PDFOptionsPanelHeader = observer(function PDFOptionsPanelHeader() {
    return (<div className={css.header}>
      Header
    </div>)
})

export const PDFOptionsPanelJobsSection = observer(function PDFOptionsPanelJobsSection() {
  const {cloudPDF: {pendingJobs, pdfsTimestampDesc}} = usePdfOptionsPanelStore()
  return (<div className={css.jobs}>
    <CollapsibleStateful title={`Pending${pendingJobs.length ? ` (${pendingJobs.length})` : ''}`} defaultOpen>
      {pendingJobs.map((job, i) => <PendingPdfJob job={job} key={i} />)}
    </CollapsibleStateful>

    <CollapsibleStateful title='Complete' defaultOpen>
      {pdfsTimestampDesc.map((entry, i) => <CompletedPdfJob entry={entry} key={entry.entry._id} />)}
    </CollapsibleStateful>

    List of jobs / artifacts (pending, running, completed)
    Ability to create a new job, select a job, open the pdf of the job, delete it, etc
  </div>)
})

export const PendingPdfJob = observer<{job: IQueuedPdfServerJob}>(function PendingPdfJob({job}) {
    const {} = usePdfOptionsPanelStore()

    return (<div className={css.pendingJob}>
      {job.status}
    </div>)
})

export const CompletedPdfJob = observer<{entry: CloudPDFEntry}>(function CompletedPdfJob({entry}) {
    const {} = usePdfOptionsPanelStore()

    return (<div className={css.pendingJob}>
      {entry.entry.filename}
    </div>)
})
export const PDFOptionsCurrentJobSection = observer(function PDFOptionsCurrentJobSection() {
  return (<div className={css.currentJob}>
    Current Job
    Submit/Cancel/Delete
    Download, open in new tab, open in flex layout tab
    Set title/subtitle/keywords/etc
    Open catalog with specific PDF query
    Inline PDF render in the sidebar
  </div>)
})


// WEBPACK FOOTER //
// ./src/modules/pdf/sideBar/PdfOptionsPanel.tsx